import { createFileRoute } from "@tanstack/react-router";
import { Binoculars, MapPin, Sparkles, Mail, Workflow, ListTodo, RefreshCcw } from "lucide-react";
import { AiGeneratorPage } from "@/components/marketing/AiGeneratorPage";

export const Route = createFileRoute("/ai/lead-finder")({
  head: () => ({
    meta: [
      { title: "Free AI Lead Finder — Find B2B Leads & Local Business Prospects | banana.cards" },
      { name: "description", content: "Find qualified B2B leads and local business prospects with AI. Search Google Maps in plain English — 'plumbers in Brooklyn with under 30 reviews' — and export to CRM. Free to try." },
      { property: "og:title", content: "Free AI Lead Finder — banana.cards" },
      { property: "og:description", content: "Find qualified B2B leads in plain English. Free AI prospecting tool — search Google Maps and export to your CRM." },
      { property: "og:url", content: "https://banana.cards/ai/lead-finder" },
    ],
    links: [{ rel: "canonical", href: "https://banana.cards/ai/lead-finder" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "AI Lead Finder",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        }),
      },
    ],
  }),
  component: Page,
});

const EXAMPLES = [
  "Plumbers in Brooklyn with fewer than 30 Google reviews and no website",
  "Dentists in Austin Texas missing online booking",
  "Coffee shops in Lisbon with no Instagram account",
  "Boutique hotels in Bali without a TripAdvisor listing",
  "Real estate agents in Miami with websites older than 5 years",
  "Yoga studios in San Francisco that don't offer a mobile app",
  "Law firms in Boston with under 50 reviews",
];

export function Page() {
  return (
    <AiGeneratorPage
      eyebrow="AI Lead Finder"
      title="Find your next 100"
      italic="customers."
      description="Describe your ideal prospect in plain English. Banana scouts Google Maps, filters by signals (website, reviews, social presence) and hands you a clean list — ready to enrich, contact and pipeline."
      headline="What kind of business would you like to prospect?"
      placeholders={EXAMPLES}
      linkedFromHref="/platform/scout"
      linkedFromLabel="Banana Scout — AI Prospecting"
      sections={[
        { icon: Sparkles, title: "Natural-language search", desc: "No filters, no boolean. 'SaaS founders in NYC under 20 employees' just works." },
        { icon: MapPin, title: "Local + B2B coverage", desc: "Powered by Google Maps + a business graph. Find local services or global B2B targets." },
        { icon: Binoculars, title: "Qualification signals", desc: "Filter by review count, website age, social presence, opening hours and more." },
      ]}
      capabilities={[
        "Search Google Maps in plain English",
        "Filter by reviews, website, socials, hours",
        "Email & phone enrichment per prospect",
        "Push selected leads into a pipeline",
        "Auto-create follow-up tasks per lead",
        "Bulk export to CSV",
        "De-dupes against your existing CRM",
        "Saves past searches for re-runs",
      ]}
      howItWorks={[
        { title: "Describe your ICP", desc: "Industry, location and any qualification signal you care about." },
        { title: "AI runs the search", desc: "Banana scans Google Maps and matching directories in seconds." },
        { title: "Review & qualify", desc: "Open each prospect, see signals, enrich with email + phone." },
        { title: "Push to your CRM", desc: "Send selected leads into a pipeline and assign tasks to your team." },
      ]}
      faq={[
        { q: "Where do the leads come from?", a: "Primarily Google Maps / Google Places — the same data that powers Google's own local search. Banana adds enrichment from a B2B business graph (firmographics, tech-stack, socials) and from open web sources, so each prospect comes with website, reviews, hours and a quality score." },
        { q: "Is this legal? What about GDPR and CCPA?", a: "Yes — Banana only surfaces publicly-available business information from Google Maps and the open web. Personal data is processed under legitimate-interest for B2B prospecting, individuals can request deletion at any time, and you can disable lookups in specific regions to match your policy." },
        { q: "How accurate is the data?", a: "Business name, address, phone, website and review counts come directly from Google and are typically 95%+ accurate. Enriched fields (email, decision-maker name, tech-stack) carry a confidence score per field — high-confidence values auto-populate, lower-confidence are flagged for review." },
        { q: "Can I export the leads?", a: "Yes — paid plans export to CSV, push to your Banana CRM pipelines, or sync via Zapier and webhooks to HubSpot, Pipedrive, Salesforce and 4,000+ apps." },
        { q: "How is this different from ZoomInfo or Apollo?", a: "Apollo and ZoomInfo are great for enterprise SaaS prospecting but expensive and weak on local SMBs. Banana Scout is built for founders, agencies, freelancers and local sales teams who need to find SMBs and local services in any city — fast, in plain English, and at a fraction of the price." },
      ]}
      flow={{
        steps: [
          {
            question: "What type of business?",
            multi: false,
            options: ["Local services (plumbers, dentists…)", "Restaurants & hospitality", "Retail & boutiques", "Real estate", "Wellness & fitness", "Professional services (law, accounting)", "Other B2B"],
          },
          {
            question: "Where should we look?",
            multi: false,
            options: ["North America", "Europe", "Asia", "LATAM", "Middle East", "Global"],
          },
          {
            question: "Which signals matter?",
            options: ["Missing website", "Low review count", "No online booking", "Outdated site", "No social presence", "Small team size"],
          },
        ],
        loadingMessages: [
          "Scanning Google Maps…",
          "Cross-checking business graph…",
          "Applying qualification filters…",
          "Enriching with contact info…",
          "Scoring prospects…",
          "De-duping against your CRM…",
          "Preparing your export…",
        ],
        readyTitle: "Your prospect list is ready.",
        readyBody: "We found ~140 qualified prospects matching your ICP. Sign up to view the full list, enrich with email + phone, and push straight to your pipeline.",
        ctaLabel: "Sign up to view leads",
        ctaHref: "https://app.banana.cards/signup",
        nextThings: [
          { icon: Mail, title: "Enrich emails & phone", desc: "One click to pull verified contact info per prospect." },
          { icon: Workflow, title: "Push to CRM pipeline", desc: "Move leads into a Banana pipeline in bulk." },
          { icon: ListTodo, title: "Auto-assign follow-ups", desc: "Create tasks per lead and split them across your team." },
          { icon: RefreshCcw, title: "Save & re-run searches", desc: "Get new prospects delivered weekly on autopilot." },
        ],
      }}
    />
  );
}
